// @flow
import * as React from 'react'
import tw from 'tailwind-styled-components'

type Props = {
  $hasError?: boolean,
  disabled?: boolean,
  grayOutWhenDisabled?: boolean,
  initialHeight?: number,
  maxHeight?: number,
  maxLength?: number,
  onChange?: Function,
  skipOwnState?: boolean,
  useEvent?: boolean,
  value?: string,
  useEvent?: boolean,
}

const StyledTextArea = tw.textarea`
  form-input
  focus:!border-primary
  focus:shadow-[0_0_0_2px_rgba(5,130,255,0.08)]
  border
  rounded-md
  block
  w-full
  transition
  duration-150
  ease-in-out
  text-gray-700
  sm:text-sm
  sm:leading-5
  resize-none

  ${(props: Props) => {
    if (!props.$hasError) return ''

    return 'border-red-500'
  }}

  ${({
    $grayOutWhenDisabled,
    disabled,
  }: {
    $grayOutWhenDisabled?: boolean,
    disabled: boolean,
  }) => {
    if ($grayOutWhenDisabled && disabled) return 'bg-gray-100'

    return ''
  }}
`

function TextArea({
  disabled,
  initialHeight,
  maxHeight,
  maxLength,
  onChange,
  skipOwnState,
  value,
  useEvent,
  ...props
}: Props): React.Node {
  const textAreaRef = React.useRef<HTMLTextAreaElement | null>(null)

  const [text, setText] = React.useState(value || '')
  const [textAreaHeight, setTextAreaHeight] = React.useState(
    initialHeight || 'auto'
  )
  const [parentHeight, setParentHeight] = React.useState('auto')

  const calculatedInputHeight = React.useCallback(() => {
    if (!textAreaRef.current) return

    const heightNumber = maxHeight
      ? Math.min(maxHeight, textAreaRef.current.scrollHeight)
      : textAreaRef.current.scrollHeight
    return `${heightNumber}px`
  }, [maxHeight, textAreaRef])

  React.useEffect(() => {
    setParentHeight(calculatedInputHeight)
    setTextAreaHeight(calculatedInputHeight)
  }, [text, calculatedInputHeight])

  React.useEffect(() => {
    if (!skipOwnState) return

    setTextAreaHeight(initialHeight || 'auto')
    setParentHeight('1px')

    setText(value)
  }, [initialHeight, value, skipOwnState])

  const handleChange = (e: SyntheticEvent<HTMLTextAreaElement>) => {
    if (disabled) return
    setTextAreaHeight(initialHeight || 'auto')
    setParentHeight(calculatedInputHeight)

    if (!skipOwnState) {
      setText(e.currentTarget.value)
    }

    if (useEvent) {
      onChange?.(e)
    } else if (onChange) {
      onChange(e.currentTarget.value)
    }
  }

  const singleLineTextValue = value || ''

  return (
    <div
      style={{
        minHeight: parentHeight,
      }}
    >
      <StyledTextArea
        {...props}
        disabled={disabled}
        maxLength={maxLength}
        onChange={handleChange}
        ref={textAreaRef}
        style={{ height: textAreaHeight }}
        value={singleLineTextValue}
      />
    </div>
  )
}

export default TextArea
